import { request } from '@/api/request'
import type { TicketListItem } from '@/api/ticketsApi'

/** Ticket boards (z8nrz7fxzq): saved filters + a grouping, private or shared. Agents only. */

export type BoardGroupBy = 'status' | 'assignee' | 'priority'
export type BoardVisibility = 'private' | 'shared'

/** The same meaning as the queue's filters (GET /tickets), as arrays. */
export interface BoardFilters {
  queue?: string
  priority?: string[]
  product?: string[]
  type?: string[]
  customer?: string[]
  status?: string[]
  q?: string
  assignee?: string
}

export interface Board {
  id: string
  name: string
  filters: BoardFilters
  groupBy: BoardGroupBy
  visibility: BoardVisibility
  owner: { id: string; name: string }
  team: { id: string; name: string } | null
  members: { id: string; name: string }[]
  /** The viewer owns it: may edit, share and delete. */
  mine: boolean
  /** A built-in queue board (queue-all, …): read-only, owned by no one. */
  system?: boolean
  description: string | null
  /** A colour key (lib/boardLook BOARD_COLORS). */
  color: string | null
  icon: string | null
  /** Work-in-progress limit per column id; over it warns, never blocks. */
  wipLimits: Record<string, number>
  archivedAt: string | null
  /** I starred it (per person). */
  starred: boolean
  /** When I last opened it. */
  lastOpenedAt: string | null
}

export interface BoardInput {
  name?: string
  filters?: BoardFilters
  groupBy?: BoardGroupBy
  visibility?: BoardVisibility
  teamId?: string | null
  memberIds?: string[]
  description?: string | null
  color?: string | null
  icon?: string | null
  wipLimits?: Record<string, number> | null
  /** true archives, false restores. */
  archived?: boolean
}

export type FolderScope = 'personal' | 'team'

export interface BoardFolder {
  id: string
  name: string
  scope: FolderScope
  team: { id: string; name: string } | null
  position: number
  /** Its boards I can see, in order. */
  boardIds: string[]
}

export interface BoardFolderTree {
  personal: BoardFolder[]
  /** My team's folders; null when I am in no team. */
  team: { id: string; name: string; folders: BoardFolder[] } | null
}

/** A card: the queue's list item plus the statuses a drag may move it to. */
export type BoardCard = TicketListItem & { allowedTransitions: string[] }

export interface BoardTickets {
  items: BoardCard[]
  total: number
  truncated: boolean
}

const json = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) })

export const boardsApi = {
  list: (): Promise<Board[]> => request<Board[]>('/boards'),
  archived: (): Promise<Board[]> => request<Board[]>('/boards?archived=true'),
  star: (id: string, starred: boolean): Promise<Board> => request(`/boards/${id}/star`, json('PUT', { starred })),
  duplicate: (id: string): Promise<Board> => request(`/boards/${id}/duplicate`, { method: 'POST' }),
  get: (id: string): Promise<Board> => request<Board>(`/boards/${id}`),
  tickets: (id: string): Promise<BoardTickets> => request<BoardTickets>(`/boards/${id}/tickets`),
  create: (input: BoardInput): Promise<Board> => request('/boards', json('POST', input)),
  update: ({ id, ...input }: BoardInput & { id: string }): Promise<Board> => request(`/boards/${id}`, json('PATCH', input)),
  remove: (id: string): Promise<void> => request(`/boards/${id}`, { method: 'DELETE' }),
  shareTargets: (): Promise<{ agents: { id: string; name: string }[]; teams: { id: string; name: string }[] }> =>
    request('/boards/share-targets'),
}

export const foldersApi = {
  tree: (): Promise<BoardFolderTree> => request<BoardFolderTree>('/board-folders'),
  create: (input: { name: string; scope: FolderScope }): Promise<BoardFolder> => request('/board-folders', json('POST', input)),
  rename: ({ id, name }: { id: string; name: string }): Promise<BoardFolder> => request(`/board-folders/${id}`, json('PATCH', { name })),
  remove: (id: string): Promise<void> => request(`/board-folders/${id}`, { method: 'DELETE' }),
  /** The folder's boards, in order (a board moves here from its sibling folder). */
  setBoards: ({ folderId, boardIds }: { folderId: string; boardIds: string[] }): Promise<BoardFolder> =>
    request(`/board-folders/${folderId}/boards`, json('PUT', { boardIds })),
  order: (input: { scope: FolderScope; folderIds: string[] }): Promise<BoardFolderTree> => request('/board-folders/order', json('PUT', input)),
}
